import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Person, People, PersonPlus, ChatDots, X, ThreeDots } from 'react-bootstrap-icons';
import ReactDOM from 'react-dom';
import './Chat.css';

function Chat({ userId, setIsAuthenticated, setUserId }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [users, setUsers] = useState([]);
  const [friends, setFriends] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);
  const [addFriendId, setAddFriendId] = useState('');
  const [csrfToken, setCsrfToken] = useState('');
  const [selectedFriend, setSelectedFriend] = useState(null);
  const [dmMessages, setDmMessages] = useState({});
  const [unreadCounts, setUnreadCounts] = useState({});
  const [typingUsers, setTypingUsers] = useState([]);
  const [dmTypingUsers, setDmTypingUsers] = useState({});
  const [expandedSection, setExpandedSection] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState('');
  const [profileInfo, setProfileInfo] = useState(null);
  const [showChatWindow, setShowChatWindow] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [hasUnreadLoungeMessages, setHasUnreadLoungeMessages] = useState(false);
  const ws = useRef(null);
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  const selectedFriendRef = useRef(null);
  const typingTimeoutRef = useRef({});
  const isTypingRef = useRef(false);
  const wsInitialized = useRef(false);
  const sidebarRef = useRef(null);
  const messagesRef = useRef(null);
  const optionsButtonRefs = useRef({});

  useEffect(() => {
    selectedFriendRef.current = selectedFriend;
  }, [selectedFriend]);

  useEffect(() => {
    if (location.pathname === '/chat') {
      setShowChatWindow(false);
      setSelectedFriend(null);
      setHasUnreadLoungeMessages(false);
    }
  }, [location.pathname]);

  const connectWebSocket = useCallback((retryCount = 0) => {
    if (ws.current && (ws.current.readyState === WebSocket.OPEN || ws.current.readyState === WebSocket.CONNECTING)) {
      return;
    }

    ws.current = new WebSocket('wss://cloudtopchat.com/ws');

    ws.current.onopen = () => {
      console.log('Connected to WebSocket server');
      reconnectAttempts.current = 0;
      wsInitialized.current = true;
    };

    ws.current.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('Received WebSocket message:', data);
        if (data.type === 'message') {
          setMessages((prev) => {
            const exists = prev.some(msg => msg.userId === data.userId && msg.content === data.content && msg.timestamp === data.timestamp);
            if (!exists) {
              if (selectedFriend || !showChatWindow) {
                setHasUnreadLoungeMessages(true);
              }
              return [...prev, {
                userId: data.userId,
                nickname: data.nickname,
                content: data.content,
                timestamp: data.timestamp,
                profilePicture: data.profilePicture || 1,
              }];
            }
            return prev;
          });
        } else if (data.type === 'user-joined' || data.type === 'user-left') {
          const uniqueUsers = [];
          const seenUserIds = new Set();
          for (const user of data.users) {
            if (!seenUserIds.has(user.userId)) {
              seenUserIds.add(user.userId);
              uniqueUsers.push(user);
            }
          }
          setUsers(uniqueUsers);
        } else if (data.type === 'friend-request-sent') {
          setFriendRequests((prev) => [...prev, { userId: data.userId, nickname: data.nickname }]);
        } else if (data.type === 'friend-request-accepted') {
          setFriendRequests((prev) => prev.filter(req => req.userId !== data.userId));
          setFriends((prev) => [...prev, { userId: data.userId, nickname: data.nickname }]);
          setNotification('Friend request accepted!');
        } else if (data.type === 'friend-removed') {
          setFriends((prev) => prev.filter(friend => friend.userId !== data.userId));
          if (selectedFriendRef.current?.userId === data.userId) {
            setSelectedFriend(null);
            setDmMessages((prev) => {
              const updatedDmMessages = { ...prev };
              delete updatedDmMessages[data.userId];
              return updatedDmMessages;
            });
          }
          setNotification('Friend removed successfully!');
        } else if (data.type === 'friend-request-ignored') {
          setFriendRequests((prev) => prev.filter(req => req.userId !== data.userId));
          setNotification('Friend request ignored!');
        } else if (data.type === 'private-message') {
          const { senderId, receiverId, nickname, content, timestamp, isRead, profilePicture } = data;
          const friendId = senderId === userId ? receiverId : senderId;
          setDmMessages((prev) => ({
            ...prev,
            [friendId]: [
              ...(prev[friendId] || []),
              { senderId, receiverId, nickname, content, timestamp, isRead, profilePicture: profilePicture || 1 },
            ],
          }));
          if (senderId !== userId && friendId !== selectedFriendRef.current?.userId) {
            setUnreadCounts((prev) => ({
              ...prev,
              [friendId]: (prev[friendId] || 0) + 1,
            }));
          }
          if (senderId !== userId && friendId === selectedFriendRef.current?.userId && ws.current && ws.current.readyState === WebSocket.OPEN) {
            ws.current.send(JSON.stringify({
              type: 'mark-messages-read',
              friendId,
            }));
          }
        } else if (data.type === 'typing-start') {
          const { userId: typingUserId, nickname, chatType, receiverId } = data;
          if (chatType === 'public') {
            setTypingUsers((prev) => {
              if (!prev.some(user => user.userId === typingUserId)) {
                return [...prev, { userId: typingUserId, nickname }];
              }
              return prev;
            });
          } else if (chatType === 'private' && receiverId === userId && selectedFriendRef.current?.userId === typingUserId) {
            setDmTypingUsers((prev) => ({
              ...prev,
              [typingUserId]: { userId: typingUserId, nickname },
            }));
          }
          if (typingTimeoutRef.current[typingUserId]) {
            clearTimeout(typingTimeoutRef.current[typingUserId]);
          }
          typingTimeoutRef.current[typingUserId] = setTimeout(() => {
            if (chatType === 'public') {
              setTypingUsers((prev) => prev.filter(user => user.userId !== typingUserId));
            } else if (chatType === 'private') {
              setDmTypingUsers((prev) => {
                const newDmTypingUsers = { ...prev };
                delete newDmTypingUsers[typingUserId];
                return newDmTypingUsers;
              });
            }
            delete typingTimeoutRef.current[typingUserId];
          }, 3000);
        } else if (data.type === 'typing-stop') {
          const { userId: typingUserId, chatType, receiverId } = data;
          if (chatType === 'public') {
            setTypingUsers((prev) => prev.filter(user => user.userId !== typingUserId));
          } else if (chatType === 'private' && receiverId === userId && selectedFriendRef.current?.userId === typingUserId) {
            setDmTypingUsers((prev) => {
              const newDmTypingUsers = { ...prev };
              delete newDmTypingUsers[typingUserId];
              return newDmTypingUsers;
            });
          }
          if (typingTimeoutRef.current[typingUserId]) {
            clearTimeout(typingTimeoutRef.current[typingUserId]);
            delete typingTimeoutRef.current[typingUserId];
          }
        }
      } catch (error) {
        console.error('Error processing WebSocket message:', error);
      }
    };

    ws.current.onclose = (event) => {
      console.log('WebSocket closed', { code: event.code, reason: event.reason });
      if (wsInitialized.current && ((event.code === 1000) || (event.code === 1008 && event.reason === 'New connection established'))) {
        return;
      } else if (event.code === 1008 && event.reason !== 'New connection established') {
        if (reconnectAttempts.current < maxReconnectAttempts) {
          reconnectAttempts.current += 1;
          setTimeout(() => connectWebSocket(reconnectAttempts.current), 5000);
        }
      } else if (reconnectAttempts.current < maxReconnectAttempts) {
        reconnectAttempts.current += 1;
        setTimeout(() => connectWebSocket(reconnectAttempts.current), 5000);
      }
    };

    ws.current.onerror = (error) => {
      console.error('WebSocket error', error);
    };
  }, [userId, selectedFriend, showChatWindow]);

  const fetchFriends = useCallback(async () => {
    try {
      const response = await fetch('/api/friends', {
        credentials: 'include',
      });
      const data = await response.json();
      if (data.success) {
        const friendsWithAvatars = await Promise.all(
          data.friends.map(async (friend) => {
            const userResponse = await fetch(`/api/user/${friend.userId}`, { credentials: 'include' });
            const userData = await userResponse.json();
            return {
              ...friend,
              profilePicture: userData.success ? userData.user.profilePicture : 1,
            };
          })
        );
        setFriends(friendsWithAvatars);
        setFriendRequests(data.friendRequests);
        setUnreadCounts(data.unreadCounts || {});
      } else if (response.status === 401) {
        setIsAuthenticated(false);
        setUserId(null);
        navigate('/login');
      }
    } catch (error) {
      console.error('Error fetching friends:', error);
    }
  }, [setIsAuthenticated, navigate, setUserId]);

  const fetchUserProfile = async () => {
    try {
      const response = await fetch('/api/me', {
        credentials: 'include',
      });
      const data = await response.json();
      if (data.success) {
        const userResponse = await fetch(`/api/user/${data.userId}`, {
          credentials: 'include',
        });
        const userData = await userResponse.json();
        if (userData.success) {
          setProfileInfo({
            nickname: userData.user.nickname,
            userId: data.userId,
            profilePicture: userData.user.profilePicture || 1,
          });
          toggleSection('profile');
        } else {
          setNotification('Failed to fetch profile: ' + userData.message);
        }
      } else {
        setNotification('Failed to fetch profile: ' + data.message);
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      setNotification('Error fetching user profile');
    }
  };

  const updateProfilePicture = async (newPictureId) => {
    if (!csrfToken) {
      setNotification('CSRF token not available. Please refresh the page and try again.');
      return;
    }

    try {
      const response = await fetch('/api/update-profile-picture', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'CSRF-Token': csrfToken,
        },
        credentials: 'include',
        body: JSON.stringify({ profilePicture: newPictureId }),
      });
      const data = await response.json();
      if (data.success) {
        setProfileInfo((prev) => ({ ...prev, profilePicture: newPictureId }));
        setNotification('Profile picture updated successfully!');
        fetchFriends(); // Update friend list avatars
      } else {
        setNotification('Failed to update profile picture: ' + data.message);
      }
    } catch (error) {
      console.error('Error updating profile picture:', error);
      setNotification('Error updating profile picture');
    }
  };

  useEffect(() => {
    const fetchPublicChatHistory = async () => {
      setIsLoading(true);
      try {
        const response = await fetch('/api/public-chat-history', {
          credentials: 'include',
        });
        const data = await response.json();
        if (data.success) {
          const welcomeMessage = {
            userId: null,
            nickname: 'System',
            content: 'Welcome to Cloud Top Chat!',
            timestamp: new Date().toISOString(),
            isSystem: true,
          };
          const reversedMessages = (data.messages || []).reverse();
          setMessages([welcomeMessage, ...reversedMessages]);
          setHasUnreadLoungeMessages(false);
        }
      } catch (error) {
        console.error('Error fetching public chat history:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchPublicChatHistory();
  }, []);

  useEffect(() => {
    if (!wsInitialized.current) {
      connectWebSocket();
    }
    return () => {
      if (ws.current && ws.current.readyState === WebSocket.OPEN) {
        ws.current.close(1000, 'Component unmounted');
      }
      wsInitialized.current = false;
    };
  }, [connectWebSocket]);

  useEffect(() => {
    const fetchCsrfToken = async () => {
      try {
        const response = await fetch('/api/csrf-token', {
          credentials: 'include',
        });
        const data = await response.json();
        setCsrfToken(data.csrfToken);
      } catch (error) {
        console.error('Failed to fetch CSRF token:', error);
      }
    };
    fetchCsrfToken();
    fetchFriends();
  }, [fetchFriends]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, dmMessages, selectedFriend, typingUsers, dmTypingUsers, showChatWindow]);

  useEffect(() => {
    if (selectedFriend && !selectedFriend.profilePicture) {
      const fetchFriendProfile = async () => {
        try {
          const response = await fetch(`/api/user/${selectedFriend.userId}`, {
            credentials: 'include',
          });
          const data = await response.json();
          if (data.success) {
            setSelectedFriend({ ...selectedFriend, profilePicture: data.user.profilePicture || 1 });
          }
        } catch (error) {
          console.error('Error fetching selected friend profile:', error);
        }
      };
      fetchFriendProfile();
    }

    if (selectedFriend) {
      const fetchChatHistory = async () => {
        setIsLoading(true);
        try {
          const response = await fetch(`/api/chat-history/${selectedFriend.userId}`, {
            credentials: 'include',
          });
          const data = await response.json();
          if (data.success) {
            setDmMessages((prev) => ({
              ...prev,
              [selectedFriend.userId]: data.messages.map(msg => ({
                senderId: msg.senderId,
                receiverId: msg.receiverId,
                nickname: msg.senderId === userId ? selectedFriend.nickname : msg.nickname || selectedFriend.nickname,
                content: msg.content,
                timestamp: msg.timestamp,
                isRead: msg.isRead,
                profilePicture: msg.senderId === userId ? profileInfo?.profilePicture : selectedFriend.profilePicture,
              })),
            }));
            if (ws.current && ws.current.readyState === WebSocket.OPEN) {
              ws.current.send(JSON.stringify({
                type: 'mark-messages-read',
                friendId: selectedFriend.userId,
              }));
            }
          }
        } catch (error) {
          console.error('Error fetching chat history:', error);
        } finally {
          setIsLoading(false);
        }
      };
      fetchChatHistory();
    }
  }, [selectedFriend, userId, profileInfo]);

  const handleTyping = (e) => {
    setNewMessage(e.target.value);
    if (!ws.current || ws.current.readyState !== WebSocket.OPEN) return;

    if (e.target.value.trim() && !isTypingRef.current) {
      isTypingRef.current = true;
      ws.current.send(JSON.stringify({
        type: 'typing-start',
        chatType: selectedFriend ? 'private' : 'public',
        receiverId: selectedFriend ? selectedFriend.userId : null,
      }));
    } else if (!e.target.value.trim() && isTypingRef.current) {
      isTypingRef.current = false;
      ws.current.send(JSON.stringify({
        type: 'typing-stop',
        chatType: selectedFriend ? 'private' : 'public',
        receiverId: selectedFriend ? selectedFriend.userId : null,
      }));
    }
  };

  const sendMessage = () => {
    if (!newMessage.trim()) return;

    if (selectedFriend) {
      if (ws.current && ws.current.readyState === WebSocket.OPEN) {
        ws.current.send(JSON.stringify({
          type: 'private-message',
          receiverId: selectedFriend.userId,
          content: newMessage.trim(),
        }));
        setNewMessage('');
        if (isTypingRef.current) {
          isTypingRef.current = false;
          ws.current.send(JSON.stringify({
            type: 'typing-stop',
            chatType: 'private',
            receiverId: selectedFriend.userId,
          }));
        }
      }
    } else {
      if (ws.current && ws.current.readyState === WebSocket.OPEN) {
        ws.current.send(JSON.stringify({
          type: 'message',
          content: newMessage.trim(),
        }));
        setNewMessage('');
        if (isTypingRef.current) {
          isTypingRef.current = false;
          ws.current.send(JSON.stringify({
            type: 'typing-stop',
            chatType: 'public',
          }));
        }
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      sendMessage();
    }
  };

  const handleAddFriend = async () => {
    if (!addFriendId || isNaN(addFriendId)) {
      setNotification('Please enter a valid user ID');
      return;
    }

    if (!csrfToken) {
      setNotification('CSRF token not available. Please refresh the page and try again.');
      return;
    }

    try {
      const response = await fetch('/api/friend/request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'CSRF-Token': csrfToken,
        },
        credentials: 'include',
        body: JSON.stringify({ receiverId: Number(addFriendId) }),
      });
      const data = await response.json();
      if (data.success) {
        setNotification('Friend request sent!');
        setAddFriendId('');
      } else {
        setNotification('Failed to send friend request: ' + data.message);
      }
    } catch (error) {
      console.error('Error sending friend request:', error);
      setNotification('Error sending friend request');
    }
  };

  const handleAcceptFriend = async (requesterId) => {
    if (!csrfToken) {
      setNotification('CSRF token not available. Please refresh the page and try again.');
      return;
    }

    try {
      const response = await fetch('/api/friend/accept', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'CSRF-Token': csrfToken,
        },
        credentials: 'include',
        body: JSON.stringify({ requesterId }),
      });
      const data = await response.json();
      if (data.success) {
        setNotification('Friend request accepted!');
        fetchFriends();
      } else {
        setNotification('Failed to accept friend request: ' + data.message);
      }
    } catch (error) {
      console.error('Error accepting friend request:', error);
      setNotification('Error accepting friend request');
    }
  };

  const handleIgnoreFriend = async (requesterId) => {
    if (!csrfToken) {
      setNotification('CSRF token not available. Please refresh the page and try again.');
      return;
    }

    try {
      const response = await fetch('/api/friend/ignore', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'CSRF-Token': csrfToken,
        },
        credentials: 'include',
        body: JSON.stringify({ requesterId }),
      });
      const data = await response.json();
      if (data.success) {
        setNotification('Friend request ignored!');
        fetchFriends();
      } else {
        setNotification('Failed to ignore friend request: ' + data.message);
      }
    } catch (error) {
      console.error('Error ignoring friend request:', error);
      setNotification('Error ignoring friend request');
    }
  };

  const handleRemoveFriend = async (friendId) => {
    if (!csrfToken) {
      setNotification('CSRF token not available. Please refresh the page and try again.');
      return;
    }

    try {
      const response = await fetch('/api/friend/remove', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'CSRF-Token': csrfToken,
        },
        credentials: 'include',
        body: JSON.stringify({ friendId }),
      });
      const data = await response.json();
      if (data.success) {
        setNotification('Friend removed successfully!');
        fetchFriends();
        setDropdownOpen(null);
      } else {
        setNotification('Failed to remove friend: ' + data.message);
      }
    } catch (error) {
      console.error('Error removing friend:', error);
      setNotification('Error removing friend');
    }
  };

  const handleSelectFriend = (friend) => {
    setSelectedFriend(friend);
    setShowChatWindow(true);
    setDropdownOpen(null);
    // These lines were added
    setUnreadCounts((prev) => ({
      ...prev,
      [friend.userId]: 0,
    }));
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify({
        type: 'mark-messages-read',
        friendId: friend.userId,
      }));
    }
  };

  const handleCloseChat = () => {
    setShowChatWindow(false);
    setSelectedFriend(null);
    setHasUnreadLoungeMessages(false);
  };

  const toggleSection = (section) => {
    setExpandedSection((prev) => (prev === section ? null : section));
  };

  const toggleDropdown = (friendId) => {
    setDropdownOpen((prev) => (prev === friendId ? null : friendId));
  };

  useEffect(() => {
    const handleScroll = (e) => {
      const target = e.target;
      if (target === sidebarRef.current || target === messagesRef.current) {
        target.classList.add('scrolling');
        clearTimeout(target.scrollTimeout);
        target.scrollTimeout = setTimeout(() => {
          target.classList.remove('scrolling');
        }, 1000);
      }
    };

    const sidebar = sidebarRef.current;
    const messages = messagesRef.current;
    if (sidebar) sidebar.addEventListener('scroll', handleScroll);
    if (messages) messages.addEventListener('scroll', handleScroll);

    return () => {
      if (sidebar) sidebar.removeEventListener('scroll', handleScroll);
      if (messages) messages.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getAvatarUrl = (profilePicture) => `/avatar${profilePicture || 1}.png`;

  const renderMessages = (msgs) => {
    const groupedMessages = [];
    let lastSender = null;

    msgs.forEach((msg, index) => {
      const isSameSender = lastSender && lastSender === (msg.senderId || msg.userId) && !msg.isSystem;
      groupedMessages.push({ ...msg, showNickname: !isSameSender && !msg.isSystem });
      lastSender = msg.isSystem ? null : (msg.senderId || msg.userId);
    });

    return groupedMessages.map((msg, index) => (
      <div
        key={index}
        className={`message ${msg.isSystem ? 'message-system' : (msg.senderId === userId || msg.userId === userId ? 'message-self' : 'message-other')}`}
      >
        {(msg.showNickname || selectedFriend) && (
          <div className="message-header">
            <img
              src={getAvatarUrl(msg.profilePicture || (msg.senderId === userId ? profileInfo?.profilePicture : selectedFriend?.profilePicture))}
              alt="Avatar"
              className="message-avatar"
            />
            {!selectedFriend && (
              <span className="message-nickname">
                {(msg.senderId === userId || msg.userId === userId) ? 'You' : msg.nickname}
              </span>
            )}
          </div>
        )}
        <span className="message-content">{msg.content}</span>
        {!msg.isSystem && (
          <span className="message-timestamp">
            {formatTimestamp(msg.timestamp)}
          </span>
        )}
      </div>
    ));
  };

  const Dropdown = ({ friendId, onRemove }) => {
    const buttonRef = optionsButtonRefs.current[friendId];
    const [position, setPosition] = useState({ top: 0, left: 0 });

    useEffect(() => {
      if (buttonRef) {
        const rect = buttonRef.getBoundingClientRect();
        setPosition({
          top: rect.top + window.scrollY,
          left: rect.right + window.scrollX,
        });
      }
    }, [buttonRef]);

    return ReactDOM.createPortal(
      <div
        className="friend-options-dropdown"
        style={{
          top: `${position.top}px`,
          left: `${position.left}px`,
        }}
      >
        <button onClick={(e) => { e.stopPropagation(); onRemove(friendId); }} className="dropdown-item remove-friend-btn">
          Remove
        </button>
      </div>,
      document.getElementById('dropdown-portal')
    );
  };

  return (
    <div className="chat-container">
      <div className="chat-sidebar" ref={sidebarRef}>
        <div className="icon-container">
          <div className="icon-wrapper">
            <Person
              size={24}
              onClick={fetchUserProfile}
              style={{ cursor: 'pointer', marginRight: '15px' }}
            />
          </div>
          <div className="icon-wrapper">
            <People
              size={24}
              onClick={() => toggleSection('friendRequests')}
              style={{ cursor: 'pointer', marginRight: '15px' }}
            />
            {friendRequests.length > 0 && <span className="request-indicator"></span>}
          </div>
          <div className="icon-wrapper">
            <PersonPlus
              size={24}
              onClick={() => toggleSection('addFriend')}
              style={{ cursor: 'pointer', marginRight: '15px' }}
            />
          </div>
          <div className="icon-wrapper">
            <ChatDots
              size={24}
              onClick={() => {
                setSelectedFriend(null);
                setShowChatWindow(true);
                setHasUnreadLoungeMessages(false);
              }}
              style={{ cursor: 'pointer' }}
            />
            {hasUnreadLoungeMessages && <span className="request-indicator"></span>}
          </div>
        </div>
        <div className="icon-sections">
          {expandedSection === 'profile' && profileInfo && (
            <div className="user-profile">
              <img
                src={getAvatarUrl(profileInfo.profilePicture)}
                alt="Profile Avatar"
                className="profile-avatar"
              />
              <p>
                <strong>Nickname:</strong> {profileInfo.nickname}
              </p>
              <p>
                <strong>User ID:</strong> {profileInfo.userId}
              </p>
              <div className="avatar-selector">
                <p><strong>Change Avatar:</strong></p>
                <div className="avatar-options">
                  {[1, 2, 3, 4, 5].map((id) => (
                    <img
                      key={id}
                      src={getAvatarUrl(id)}
                      alt={`Avatar ${id}`}
                      className={`avatar-option ${profileInfo.profilePicture === id ? 'selected' : ''}`}
                      onClick={() => updateProfilePicture(id)}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
          {expandedSection === 'friendRequests' && (
            <div className="friend-requests">
              {friendRequests.length > 0 ? (
                <ul>
                  {friendRequests.map(request => (
                    <li key={request.userId}>
                      <span>{request.nickname} (ID: {request.userId})</span>
                      <div className="friend-request-actions">
                        <button onClick={() => handleAcceptFriend(request.userId)} className="accept-friend-btn">Accept</button>
                        <button onClick={() => handleIgnoreFriend(request.userId)} className="ignore-friend-btn">Ignore</button>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>No requests recently</p>
              )}
            </div>
          )}
          {expandedSection === 'addFriend' && (
            <div className="add-friend">
              <input
                type="text"
                value={addFriendId}
                onChange={(e) => setAddFriendId(e.target.value)}
                onKeyPress={(e) => {
                  const charCode = e.charCode;
                  if (charCode < 48 || charCode > 57) {
                    e.preventDefault();
                  }
                }}
                placeholder="Enter user ID"
              />
              <button onClick={handleAddFriend}>Send Request</button>
            </div>
          )}
        </div>
        <div className="friend-list">
          <h3>Friends</h3>
          <ul>
            {friends.map(friend => (
              <li
                key={friend.userId}
                onClick={() => handleSelectFriend(friend)}
                className={selectedFriend?.userId === friend.userId ? 'selected-friend' : ''}
              >
                <img
                  src={getAvatarUrl(friend.profilePicture)}
                  alt={`${friend.nickname}'s avatar`}
                  className="friend-avatar"
                />
                <span>{friend.nickname} (ID: {friend.userId})</span>
                {unreadCounts[friend.userId] > 0 && (
                  <span className="unread-count">{unreadCounts[friend.userId]}</span>
                )}
                <div className="friend-options">
                  <button
                    ref={(el) => (optionsButtonRefs.current[friend.userId] = el)}
                    onClick={(e) => { e.stopPropagation(); toggleDropdown(friend.userId); }}
                    className="options-btn"
                  >
                    <ThreeDots size={16} />
                  </button>
                  {dropdownOpen === friend.userId && (
                    <Dropdown friendId={friend.userId} onRemove={handleRemoveFriend} />
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="chat-main">
        {showChatWindow ? (
          <>
            <div className="chat-header">
              <h2>{selectedFriend ? selectedFriend.nickname : 'Lounge'}</h2>
              <button className="close-chat-btn" onClick={handleCloseChat}>
                <X size={24} />
              </button>
            </div>
            {notification && <div className="notification">{notification}</div>}
            {!selectedFriend && (
              <div className="online-users">
                <h3>Online Users</h3>
                <ul>
                  {users.map((user, index) => (
                    <li key={`${user.userId}-${index}`} className={user.userId === userId ? 'current-user' : ''}>
                      {user.nickname} (ID: {user.userId}) {user.userId === userId ? '(You)' : ''}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="chat-messages" ref={messagesRef}>
              {isLoading ? (
                <div className="loading-spinner">
                  <div className="spinner"></div>
                  <p>Loading chat history...</p>
                </div>
              ) : (
                <>
                  {renderMessages(selectedFriend ? dmMessages[selectedFriend.userId] || [] : messages)}
                  {selectedFriend ? (
                    dmTypingUsers[selectedFriend.userId] && (
                      <div className="typing-indicator">
                        {dmTypingUsers[selectedFriend.userId].nickname} is typing...
                      </div>
                    )
                  ) : (
                    typingUsers.length > 0 && (
                      <div className="typing-indicator">
                        {typingUsers.map(user => user.nickname).join(', ')} {typingUsers.length === 1 ? 'is' : 'are'} typing...
                      </div>
                    )
                  )}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>
            <div className="chat-input">
              <input
                type="text"
                value={newMessage}
                onChange={handleTyping}
                onKeyPress={handleKeyPress}
                placeholder={selectedFriend ? `Message ${selectedFriend.nickname}...` : 'Type a message in Lounge...'}
              />
              <button onClick={sendMessage}>Send</button>
            </div>
          </>
        ) : (
          <div className="chat-cover-page">
            <span className="decorative-icon chat-bubble">💬</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default Chat;